import { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { TyreAPI, TyreDto, TyreFilterDto, TyreOrderBy, PaginationRequest } from "../api/tyreApi";
import { BasketAPI } from "../api/basketApi";
import { ShoppingCart, Plus, Minus } from "lucide-react";

const TyresPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [tyres, setTyres] = useState<TyreDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<TyreFilterDto>({});
  const [pagination, setPagination] = useState<PaginationRequest>({
    page: 1,
    pageSize: 20,
  });
  const [totalPages, setTotalPages] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  
  // Фильтры для выбора
  const [availableBrands, setAvailableBrands] = useState<string[]>([]);
  const [availableWidths, setAvailableWidths] = useState<number[]>([]);
  const [availableProfiles, setAvailableProfiles] = useState<number[]>([]);
  const [availableDiameters, setAvailableDiameters] = useState<number[]>([]);
  
  const [addingToCart, setAddingToCart] = useState<Record<number, boolean>>({});
  const [inCart, setInCart] = useState<Record<number, { itemId: number; quantity: number }>>({});
  const [showFilters, setShowFilters] = useState(false);

  // Загружаем фильтры из URL при монтировании
  useEffect(() => {
    const seasonParam = searchParams.get('season');
    if (seasonParam) {
      setFilter(prev => ({ ...prev, season: seasonParam }));
    }
  }, [searchParams]);

  useEffect(() => {
    loadFilterOptions();
  }, []);

  useEffect(() => {
    loadTyres();
  }, [filter, pagination.page, pagination.pageSize]);

  useEffect(() => {
    if (tyres.length > 0) {
      checkCartItems();
    }
  }, [tyres]);

  const checkCartItems = async () => {
    try {
      const items = await BasketAPI.getItems();
      const cartMap: Record<number, { itemId: number; quantity: number }> = {};
      
      // Создаем мапу кодов шин для быстрого поиска
      const tyreCodeMap = new Map<string, number>();
      tyres.forEach(tyre => {
        if (tyre.uniqueSKU) tyreCodeMap.set(tyre.uniqueSKU, tyre.id);
        if (tyre.vendorSKU) tyreCodeMap.set(tyre.vendorSKU, tyre.id);
        tyreCodeMap.set(`TYRE_${tyre.id}`, tyre.id);
      });
      
      items.forEach((item: any) => {
        // Проверяем, является ли это шиной
        const tyreId = tyreCodeMap.get(item.code);
        if (tyreId) {
          cartMap[tyreId] = { itemId: item.id, quantity: item.count };
        } else {
          // Также проверяем по паттерну TYRE_{id}
          const tyreIdMatch = item.code?.match(/TYRE_(\d+)/);
          if (tyreIdMatch) {
            const id = parseInt(tyreIdMatch[1]);
            cartMap[id] = { itemId: item.id, quantity: item.count };
          }
        }
      });
      setInCart(cartMap);
    } catch (error) {
      console.error("Ошибка проверки корзины:", error);
    }
  };

  const loadFilterOptions = async () => {
    try {
      const [brands, widths, profiles, diameters] = await Promise.all([
        TyreAPI.getAvailableBrands(),
        TyreAPI.getAvailableWidths(),
        TyreAPI.getAvailableProfiles(),
        TyreAPI.getAvailableDiameters(),
      ]);
      setAvailableBrands(brands);
      setAvailableWidths(widths);
      setAvailableProfiles(profiles);
      setAvailableDiameters(diameters);
    } catch (error) {
      console.error("Ошибка загрузки опций фильтров:", error);
    }
  };

  const loadTyres = async () => {
    try {
      setLoading(true);
      const response = await TyreAPI.getTyresWithFilter(filter, pagination);
      setTyres(response.data);
      setTotalPages(response.totalPages);
      setTotalCount(response.totalCount);
    } catch (error) {
      console.error("Ошибка загрузки шин:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key: keyof TyreFilterDto, value: any) => {
    setFilter((prev) => ({ ...prev, [key]: value }));
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const handleAddToCart = async (tyre: TyreDto) => {
    if (addingToCart[tyre.id] || inCart[tyre.id]) return;

    try {
      setAddingToCart((prev) => ({ ...prev, [tyre.id]: true }));
      
      const result = await BasketAPI.addTyreItem(tyre.id, 1);
      if (result) {
        setInCart((prev) => ({ ...prev, [tyre.id]: { itemId: result.id, quantity: 1 } }));
      }
    } catch (error) {
      console.error("Ошибка добавления в корзину:", error);
    } finally {
      setAddingToCart((prev) => ({ ...prev, [tyre.id]: false }));
    }
  };

  const handleUpdateQuantity = async (tyre: TyreDto, newQuantity: number) => {
    const cartItem = inCart[tyre.id];
    if (!cartItem || newQuantity < 1 || newQuantity > tyre.residue) return;

    try {
      await BasketAPI.updateItem(cartItem.itemId, newQuantity);
      setInCart((prev) => ({
        ...prev,
        [tyre.id]: { ...cartItem, quantity: newQuantity }
      }));
    } catch (error) {
      console.error("Ошибка обновления количества:", error);
      checkCartItems();
    }
  };

  const formatTyreSize = (tyre: TyreDto) => {
    return `${tyre.width}/${tyre.profile} R${tyre.diameter}`;
  };

  const handleTyreClick = (tyre: TyreDto) => {
    navigate(`/tyre/${tyre.id}`);
  };

  return (
    <div className="w-full px-4 py-8">
      <div className="mb-6 max-w-7xl mx-auto">
        <h1 className="text-3xl font-bold mb-2">Каталог шин</h1>
      </div>

      {/* Кнопка для открытия фильтров на мобильных */}
      <div className="md:hidden mb-4">
        <button
          onClick={() => setShowFilters(!showFilters)}
          className="w-full bg-white border rounded-lg px-4 py-3 flex items-center justify-between hover:bg-gray-50"
        >
          <span className="font-medium">Фильтры</span>
          <svg
            className={`w-5 h-5 transition-transform ${showFilters ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>
      </div>

      <div className="flex flex-col md:flex-row gap-6 max-w-full">
        {/* Фильтры - на мобильных сверху, на десктопе слева */}
        <div className={`${showFilters ? 'block' : 'hidden'} md:block w-full md:w-64 flex-shrink-0`}>
          <div className="bg-white border rounded-lg p-4 md:sticky md:top-4 max-w-full">
            <div className="flex items-center justify-between mb-4 md:block">
              <h2 className="text-lg font-semibold">Фильтры</h2>
              <button
                onClick={() => setShowFilters(false)}
                className="md:hidden text-gray-500 hover:text-gray-700"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Бренд</label>
                <select
                  value={filter.brand || ""}
                  onChange={(e) => handleFilterChange("brand", e.target.value || undefined)}
                  className="w-full border rounded px-3 py-2 text-sm"
                >
                  <option value="">Все бренды</option>
                  {availableBrands.map((brand) => (
                    <option key={brand} value={brand}>
                      {brand}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Ширина</label>
                <select
                  value={filter.width || ""}
                  onChange={(e) => handleFilterChange("width", e.target.value ? parseFloat(e.target.value) : undefined)}
                  className="w-full border rounded px-3 py-2 text-sm"
                >
                  <option value="">Все</option>
                  {availableWidths.map((width) => (
                    <option key={width} value={width}>
                      {width}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Профиль</label>
                <select
                  value={filter.profile || ""}
                  onChange={(e) => handleFilterChange("profile", e.target.value ? parseFloat(e.target.value) : undefined)}
                  className="w-full border rounded px-3 py-2 text-sm"
                >
                  <option value="">Все</option>
                  {availableProfiles.map((profile) => (
                    <option key={profile} value={profile}>
                      {profile}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Диаметр</label>
                <select
                  value={filter.diameter || ""}
                  onChange={(e) => handleFilterChange("diameter", e.target.value ? parseFloat(e.target.value) : undefined)}
                  className="w-full border rounded px-3 py-2 text-sm"
                >
                  <option value="">Все</option>
                  {availableDiameters.map((diameter) => (
                    <option key={diameter} value={diameter}>
                      {diameter}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Сезон</label>
                <select
                  value={filter.season || ""}
                  onChange={(e) => handleFilterChange("season", e.target.value || undefined)}
                  className="w-full border rounded px-3 py-2 text-sm"
                >
                  <option value="">Все</option>
                  <option value="Летние">Летние</option>
                  <option value="Зимние">Зимние</option>
                  <option value="Всесезонные">Всесезонные</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Сортировка</label>
                <select
                  value={filter.orderBy || TyreOrderBy.None}
                  onChange={(e) => handleFilterChange("orderBy", parseInt(e.target.value) as TyreOrderBy)}
                  className="w-full border rounded px-3 py-2 text-sm"
                >
                  <option value={TyreOrderBy.None}>По умолчанию</option>
                  <option value={TyreOrderBy.PriceAsc}>Цена: по возрастанию</option>
                  <option value={TyreOrderBy.PriceDesc}>Цена: по убыванию</option>
                  <option value={TyreOrderBy.QuantityAsc}>Остаток: по возрастанию</option>
                  <option value={TyreOrderBy.QuantityDesc}>Остаток: по убыванию</option>
                </select>
              </div>

              <button
                onClick={() => {
                  setFilter({});
                  setPagination((prev) => ({ ...prev, page: 1 }));
                }}
                className="w-full text-sm text-gray-600 hover:text-gray-800 underline text-left"
              >
                Сбросить фильтры
              </button>
            </div>
          </div>
        </div>

        {/* Каталог шин - на мобильных внизу, на десктопе справа */}
        <div className="flex-1">

      {loading ? (
        <div className="text-center py-12">Загрузка...</div>
      ) : tyres.length === 0 ? (
        <div className="text-center py-12 text-gray-500">Шины не найдены</div>
      ) : (
        <>
          <div className="mb-4 text-sm text-gray-600">
            Найдено: {totalCount} {totalCount === 1 ? "шина" : totalCount < 5 ? "шины" : "шин"}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {tyres.map((tyre) => (
              <div 
                key={tyre.id} 
                className="bg-white border rounded-lg p-4 hover:shadow-lg transition-shadow cursor-pointer"
                onClick={() => handleTyreClick(tyre)}
              >
                {tyre.image ? (
                  <img
                    src={tyre.image}
                    alt={tyre.name || "Шина"}
                    className="w-full h-48 object-contain mb-4 max-h-48"
                  />
                ) : (
                  <div className="w-full h-48 bg-gray-100 rounded-lg flex flex-col items-center justify-center text-gray-400 mb-4">
                    <svg className="w-16 h-16 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    <span className="text-xs">Нет изображения</span>
                  </div>
                )}
                <div className="mb-2">
                  <h3 className="font-semibold text-lg">{tyre.brand}</h3>
                  <p className="text-sm text-gray-600">{tyre.name}</p>
                </div>
                <div className="mb-2">
                  <p className="text-lg font-bold">{formatTyreSize(tyre)}</p>
                  {tyre.loadIndex && tyre.speedIndex && (
                    <p className="text-sm text-gray-600">
                      {tyre.loadIndex} {tyre.speedIndex}
                    </p>
                  )}
                </div>
                <div className="mb-4">
                  <p className="text-xl font-bold text-red-600">{tyre.price.toLocaleString()} ₽</p>
                  <p className="text-sm text-gray-600">Остаток: {tyre.residue} шт.</p>
                </div>
                {inCart[tyre.id] ? (
                  <div className="flex items-center gap-2">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleUpdateQuantity(tyre, inCart[tyre.id].quantity - 1);
                      }}
                      disabled={inCart[tyre.id].quantity <= 1}
                      className="flex items-center justify-center w-8 h-8 rounded-md border border-gray-300 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <Minus className="w-3 h-3" />
                    </button>
                    <input
                      type="number"
                      min="1"
                      max={tyre.residue}
                      value={inCart[tyre.id].quantity}
                      onChange={(e) => {
                        const newQuantity = parseInt(e.target.value) || 1;
                        if (newQuantity >= 1 && newQuantity <= tyre.residue) {
                          handleUpdateQuantity(tyre, newQuantity);
                        }
                      }}
                      onClick={(e) => e.stopPropagation()}
                      className="flex-1 text-center border rounded-md px-2 py-1 text-sm"
                    />
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleUpdateQuantity(tyre, inCart[tyre.id].quantity + 1);
                      }}
                      disabled={inCart[tyre.id].quantity >= tyre.residue}
                      className="flex items-center justify-center w-8 h-8 rounded-md border border-gray-300 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <Plus className="w-3 h-3" />
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleAddToCart(tyre);
                    }}
                    disabled={addingToCart[tyre.id] || tyre.residue === 0}
                    className={`w-full py-2 px-4 rounded-md font-medium flex items-center justify-center gap-2 ${
                      addingToCart[tyre.id]
                        ? "bg-gray-400 text-white cursor-not-allowed"
                        : tyre.residue === 0
                        ? "bg-gray-300 text-gray-600 cursor-not-allowed"
                        : "bg-gray-800 text-white hover:bg-gray-900"
                    }`}
                  >
                    {addingToCart[tyre.id] ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        Добавление...
                      </>
                    ) : (
                      <>
                        <ShoppingCart className="w-4 h-4" />
                        В корзину
                      </>
                    )}
                  </button>
                )}
              </div>
            ))}
          </div>

          {totalPages > 1 && (
            <div className="mt-8 flex justify-center gap-2">
              <button
                onClick={() => setPagination((prev) => ({ ...prev, page: prev.page - 1 }))}
                disabled={pagination.page === 1}
                className="px-4 py-2 border rounded disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Назад
              </button>
              <span className="px-4 py-2">
                Страница {pagination.page} из {totalPages}
              </span>
              <button
                onClick={() => setPagination((prev) => ({ ...prev, page: prev.page + 1 }))}
                disabled={pagination.page >= totalPages}
                className="px-4 py-2 border rounded disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Вперед
              </button>
            </div>
          )}
        </>
      )}
        </div>
      </div>

    </div>
  );
};

export default TyresPage;

